import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { forwardRef, useMemo } from "react";
import { Icon } from "@flatfile/design-system";
import { useDataBufferContext } from "../../context/useDataBuffer.js";
import { useTableScroller } from "../../context/useTableScroller.js";
import { resetSelectedCellRange } from "../../hooks/useSelectCells.js";
import Cell from "../Cell/Cell.js";
import { AddNewRecordStickyFooterWrapper, AddNewRecordPinnedCellWrapper, AddNewRecordButton } from "./Styles.js";
import { getPinnedColumnsWidth, getCellKey } from "./utils.js";
const MemoizedCell = React.memo(Cell);
const AddNewRecordRow = forwardRef(
  (props, ref) => {
    var _a;
    const rowData = React.useRef({});
    const [isAddingNewRow, setIsAddingNewRow] = React.useState(false);
    const { buffer } = useDataBufferContext();
    const { scrollToItem } = useTableScroller();
    const pinnedColumnsWidth = useMemo(
      () => getPinnedColumnsWidth(props.itemData),
      [props.itemData, props.pinnedColumns]
    );
    const sortedColumns = useMemo(() => {
      if (!props.pinnedColumns || props.pinnedColumns.length === 0) {
        return props.itemData.columns;
      }
      const pinnedIndices = new Set(props.pinnedColumns);
      const pinnedColumns = props.itemData.columns.filter(
        (_, index) => pinnedIndices.has(index)
      );
      const nonPinnedColumns = props.itemData.columns.filter(
        (_, index) => !pinnedIndices.has(index)
      );
      return [...pinnedColumns, ...nonPinnedColumns];
    }, [props.itemData.columns, props.pinnedColumns]);
    const updateCell = (value, column, closeRow) => {
      rowData.current = { ...rowData.current, [column]: { value } };
      if (closeRow) {
        handleAddNewRow();
      }
    };
    const handleAddNewRow = () => {
      const hasData = Object.keys(rowData.current).length > 0;
      if (hasData) {
        const cellChanges = props.itemData.columns.map((column, index) => {
          var _a2;
          return {
            row: props.itemData.totalRowCount,
            rowIndex: props.itemData.totalRowCount + 1,
            position: props.itemData.totalRowCount,
            column: index,
            ghost: true,
            value: ((_a2 = rowData.current[column.value]) == null ? void 0 : _a2.value) ?? column.defaultValue
          };
        });
        buffer.updateCells(cellChanges);
        resetSelectedCellRange();
        scrollToItem(props.itemData.totalRowCount + 2, 0);
      }
      rowData.current = {};
      setIsAddingNewRow(false);
    };
    const handleClick = () => {
      rowData.current = {};
      setIsAddingNewRow(!isAddingNewRow);
    };
    const indexRowCellStyle = useMemo(
      () => ({
        height: props.itemData.rowHeight,
        position: "absolute",
        top: 0
      }),
      [props.itemData.rowHeight]
    );
    return /* @__PURE__ */ jsx(
      AddNewRecordStickyFooterWrapper,
      {
        ref,
        bottom: !!props.itemData.decorateFooterCell && isAddingNewRow ? props.itemData.rowHeight - 1 : 0,
        isAdding: isAddingNewRow,
        width: props.width,
        "data-add-new-row-wrapper": "true",
        children: /* @__PURE__ */ jsxs("div", { style: { position: "relative" }, children: [
          /* @__PURE__ */ jsxs(
            AddNewRecordPinnedCellWrapper,
            {
              width: isAddingNewRow ? pinnedColumnsWidth : props.itemData.theme.indexColumn.width,
              children: [
                /* @__PURE__ */ jsxs(
                  AddNewRecordButton,
                  {
                    width: props.itemData.theme.indexColumn.width,
                    onClick: handleClick,
                    "data-close-add-new-row": isAddingNewRow ? "true" : void 0,
                    children: [
                      isAddingNewRow ? /* @__PURE__ */ jsx(Icon, { name: "cross", size: 12 }) : /* @__PURE__ */ jsx(Icon, { name: "plus", size: 12 }),
                      " "
                    ]
                  }
                ),
                isAddingNewRow && ((_a = props.pinnedColumns) == null ? void 0 : _a.map((colIndex, index) => {
                  var _a2;
                  const columnStyle = props.itemData.columnStyles[index];
                  const column = props.itemData.columns[colIndex];
                  return /* @__PURE__ */ jsx(
                    MemoizedCell,
                    {
                      data: {
                        ...props.itemData,
                        decorateCell: props.itemData.decorateCell,
                        onUpdateCell: (value, closeRow) => updateCell(value, column.value, closeRow),
                        onAddNewRow: handleAddNewRow,
                        cell: {
                          value: ((_a2 = rowData.current[column.value]) == null ? void 0 : _a2.value) ?? ""
                        },
                        isNewRowCell: true
                      },
                      columnIndex: index,
                      rowIndex: -1,
                      style: {
                        ...indexRowCellStyle,
                        left: columnStyle.left - props.itemData.columnStyles[0].left,
                        width: columnStyle.width
                      },
                      pinned: true
                    },
                    getCellKey(-1, index, "footer")
                  );
                }))
              ]
            }
          ),
          isAddingNewRow && /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                display: "flex",
                marginLeft: props.itemData.columnStyles[0].left
              },
              children: sortedColumns.map((column, index) => {
                var _a2, _b;
                const originalIndex = props.itemData.columns.findIndex(
                  (col) => col.value === column.value
                );
                const columnStyle = props.itemData.columnStyles[originalIndex];
                if ((_a2 = props.pinnedColumns) == null ? void 0 : _a2.includes(originalIndex)) {
                  return null;
                }
                return /* @__PURE__ */ jsx(
                  MemoizedCell,
                  {
                    data: {
                      ...props.itemData,
                      decorateCell: props.itemData.decorateCell,
                      onUpdateCell: (value, closeRow) => updateCell(value, column.value, closeRow),
                      onAddNewRow: handleAddNewRow,
                      cell: {
                        value: ((_b = rowData.current[column.value]) == null ? void 0 : _b.value) ?? ""
                      },
                      isNewRowCell: true
                    },
                    columnIndex: originalIndex,
                    rowIndex: -1,
                    style: {
                      ...indexRowCellStyle,
                      left: columnStyle.left - props.itemData.columnStyles[0].left,
                      width: columnStyle.width
                    }
                  },
                  getCellKey(-1, originalIndex, "new-record-row")
                );
              })
            }
          )
        ] })
      }
    );
  }
);
AddNewRecordRow.displayName = "AddNewRecordRow";
export {
  AddNewRecordRow
};
